import type { ReactNode } from 'react';
import { openHoursErrors, type OpenHours, type OpenHoursDay } from '@ehr/shared';
import { TimeField } from './DateTimeField.js';
import { personalTimeFormat } from '../lib/timePrefs.js';

/**
 * ehr#638 (Chase 10-24): Settings → Practice info → Open hours. One row per day: Open, Opens,
 * Closes. The hours are the primary location's (locations.hours, the one copy): Practice info edits
 * them in place, and changing the primary location shows that location's hours. Data & settings'
 * Save changes saves them with the rest of Practice info.
 *
 * ehr#637/#638 (Chase 10-25): the same editor also opens per location in Settings → Locations
 * (variant 'location'), saving that location's hours. The primary location's row edits the very
 * hours Practice info shows, so the two always agree.
 */

const SHORT_DAY: Record<OpenHoursDay['day'], string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

/** 'HH:MM' as '9:00 AM', or '09:00' for a person on 24-hour. */
function hoursTime(v: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(v);
  if (!m) return v;
  if (personalTimeFormat() === '24h') return v;
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

/**
 * ehr#637/#638 - one line for a location's hours in the Locations list: runs of days with the same
 * times are joined ("Mon-Fri 9:00 AM-5:00 PM, Sat 10:00 AM-2:00 PM"). null = never set.
 */
export function openHoursSummary(hours: OpenHours | null | undefined): string {
  if (!hours) return 'Not set';
  const parts: string[] = [];
  let i = 0;
  while (i < hours.length) {
    const h = hours[i]!;
    if (!h.open) {
      i++;
      continue;
    }
    let j = i;
    while (
      j + 1 < hours.length &&
      hours[j + 1]!.open &&
      hours[j + 1]!.start === h.start &&
      hours[j + 1]!.end === h.end
    )
      j++;
    const days = i === j ? SHORT_DAY[h.day] : `${SHORT_DAY[h.day]}-${SHORT_DAY[hours[j]!.day]}`;
    parts.push(`${days} ${hoursTime(h.start)}-${hoursTime(h.end)}`);
    i = j + 1;
  }
  return parts.length ? parts.join(', ') : 'Closed every day';
}

/** A day changed. Opening a day with no times fills 9 to 5, so it is never open with blanks. */
export function withDayChange(
  hours: OpenHours,
  i: number,
  patch: Partial<Omit<OpenHoursDay, 'day'>>,
): OpenHours {
  return hours.map((h, j) => {
    if (j !== i) return h;
    const next = { ...h, ...patch };
    if (patch.open && !h.open && !next.start && !next.end) {
      next.start = '09:00';
      next.end = '17:00';
    }
    return next;
  });
}

/**
 * The Open hours editor: a section of the Practice info pane (the primary location's hours), or
 * (variant 'location') the panel under a location's row in Settings → Locations.
 */
export function PracticeHoursSection({
  hours,
  onChange,
  locationName,
  saved,
  canEdit,
  variant = 'practice',
  isPrimary = false,
  footer,
}: {
  hours: OpenHours;
  onChange: (next: OpenHours) => void;
  /** The primary location's name, or null when the practice has no location yet. */
  locationName: string | null;
  /** false = these hours were never saved (the 9 to 5 starting point is showing). */
  saved: boolean;
  canEdit: boolean;
  /** 'practice' = Practice info (the primary location); 'location' = one row in Locations. */
  variant?: 'practice' | 'location';
  /** variant 'location': this is the primary location (its hours are Practice info's too). */
  isPrimary?: boolean;
  /** Buttons under the table (the Locations panel's Save hours / Cancel). */
  footer?: ReactNode;
}) {
  const errors = openHoursErrors(hours);
  const off = !canEdit || !locationName;
  const perLocation = variant === 'location';
  const hint = perLocation
    ? isPrimary
      ? `${locationName}'s hours. This is the primary location, so they are also the practice's open hours in Practice info`
      : `${locationName}'s own hours`
    : locationName
      ? `The hours of the primary location, ${locationName}`
      : 'Add a location first: open hours belong to the primary location';
  return (
    <div className={perLocation ? 'sec ds-hours ds-hours-loc' : 'sec ds-hours'}>
      <div className="sec-head">
        <h4>Open hours</h4>
        <span>{hint}</span>
      </div>
      {locationName && !saved && (
        <p className="ds-hours-note">
          Not saved yet. Monday to Friday, 9 to 5 is a starting point; change it and press{' '}
          {perLocation ? 'Save hours' : 'Save changes'}.
        </p>
      )}
      <table className="tbl ds-hours-table">
        <thead>
          <tr>
            <th scope="col">Day</th>
            <th scope="col">Open</th>
            <th scope="col">Opens</th>
            <th scope="col">Closes</th>
          </tr>
        </thead>
        <tbody>
          {hours.map((h, i) => (
            <tr key={h.day} className={h.open ? '' : 'closed'}>
              <th scope="row">{h.day}</th>
              <td>
                <label className="sw">
                  <input
                    type="checkbox"
                    aria-label={`Open on ${h.day}`}
                    checked={h.open}
                    disabled={off}
                    onChange={(e) => onChange(withDayChange(hours, i, { open: e.target.checked }))}
                  />
                  <span />
                </label>
              </td>
              {h.open ? (
                <>
                  <td>
                    <TimeField
                      className="ds-hours-time"
                      value={h.start}
                      step={5}
                      disabled={off}
                      ariaLabel={`${h.day} opens`}
                      onChange={(v) => onChange(withDayChange(hours, i, { start: v }))}
                    />
                  </td>
                  <td>
                    <TimeField
                      className="ds-hours-time"
                      value={h.end}
                      step={5}
                      disabled={off}
                      ariaLabel={`${h.day} closes`}
                      onChange={(v) => onChange(withDayChange(hours, i, { end: v }))}
                    />
                  </td>
                </>
              ) : (
                <td colSpan={2} className="muted">
                  Closed
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {errors.length > 0 && (
        <p className="ds-hours-err" role="alert">
          {errors.join(' ')}
        </p>
      )}
      {footer}
    </div>
  );
}
